import type {
  BdProgressCounts,
  BdUiProgressStep,
  BdUiProgressStepState,
} from '@bindoc-projects/ui-lib/feedback/ui';
import type {
  BdJobChainResponse,
  BdJobChainSummaryResponse,
  BdJobTaskError,
  BdJobTaskErrorReason,
  BdJobTaskStatusResponse,
} from './chain.model';

/** German step labels per task type; unknown types fall back to a generic step. */
export const BD_JOB_TASK_LABELS: Readonly<Record<string, string>> = {
  'de-virtual-clinic.p21-import': 'Daten importieren',
  'de-virtual-clinic.referral-import': 'Zuweiser importieren',
  'de-virtual-clinic.intuitive-surgical-import': 'Intuitive-Surgical-Daten importieren',
  'de-virtual-clinic.intuitive-surgical-shadow-refresh': 'Falldaten aktualisieren',
  'de-virtual-clinic.decision-cockpit-import': 'Decision-Cockpit-Daten importieren',
  'de-virtual-clinic.splitter': 'Daten auf Kliniken aufteilen',
  'de-virtual-clinic.case-shadow-refresh': 'Falldaten aktualisieren',
  'de-virtual-clinic.analysis-cache-filler': 'Analysen vorberechnen',
  'de-virtual-clinic.analysis-mark-for-recalculation': 'Neuberechnung vormerken',
  'de-virtual-clinic.referral-export': 'Zuweiser exportieren',
  'de-virtual-clinic.fully-inpatient-case-master-export': 'Fälle exportieren',
  'de-virtual-clinic.decision-cockpit-export': 'Decision Cockpit exportieren',
  'de-system.owner-content-purge': 'Inhalte endgültig löschen',
  'de-system.user-delete-db': 'Nutzer aus der Datenbank löschen',
  'de-system.user-reference-scrub': 'Verweise auf den Nutzer entfernen',
  'de-system.user-b2c-delete': 'Anmeldekonto löschen',
  'de-system.company-delete-db': 'Kunde aus der Datenbank löschen',
  'de-system.company-b2c-delete': 'Kundengruppe im Anmeldesystem löschen',
};

/** German names of chain kinds; unknown kinds show as they are. */
export const BD_JOB_CHAIN_KIND_LABELS: ReadonlyMap<string, string> = new Map([
  ['user-data-graph.owner-content-purge', 'Inhalte endgültig löschen'],
  ['user-data-graph.user-delete', 'Nutzer löschen'],
  ['user-data-graph.company-delete', 'Kunde löschen'],
]);

export function chainKindLabel(kind: string): string {
  return BD_JOB_CHAIN_KIND_LABELS.get(kind) ?? kind;
}

const FALLBACK_TASK_LABEL = 'Verarbeitungsschritt';

const ERROR_TEXT: Readonly<Record<BdJobTaskErrorReason, string>> = {
  'handler-failed': 'Bei der Verarbeitung ist ein Fehler aufgetreten.',
  'result-not-serializable': 'Die Verarbeitung wurde unerwartet abgebrochen.',
  'no-handler': 'Die Verarbeitung wurde unerwartet abgebrochen.',
  'vm-error': 'Die Verarbeitung wurde unerwartet abgebrochen.',
  'lease-expired': 'Die Verarbeitung wurde unerwartet abgebrochen.',
  superseded: 'Die Verarbeitung wurde durch einen neueren Auftrag ersetzt.',
  cancelled: 'Die Verarbeitung wurde abgebrochen.',
};

export interface BdJobChainStepsOptions {
  /**
   * The chain finished and the caller reads its result: a last running step
   * "Ergebnisse werden geladen" keeps the view instead of switching to a
   * spinner.
   */
  readingResult?: boolean;
  /** Labels per task type, over {@link BD_JOB_TASK_LABELS}. */
  labels?: Readonly<Record<string, string>>;
}

/**
 * Bookkeeping members the user does not wait for: `mark-error` even ends
 * SUCCESS on a failed chain, so it must never read as a step done.
 */
export function isBookkeepingTask(type: string): boolean {
  return type.endsWith('.mark-error') || type.endsWith('-notification');
}

export function taskTypeLabel(
  type: string,
  labels: Readonly<Record<string, string>> = BD_JOB_TASK_LABELS,
): string {
  return labels[type] ?? BD_JOB_TASK_LABELS[type] ?? FALLBACK_TASK_LABEL;
}

/**
 * Maps a chain onto the steps of `bd-ui-step-progress`. Members of one type
 * form one step — the cache fill runs once per target clinic — that is done
 * once all of them succeeded and shows `n/m` while it runs. The percentage and
 * message are the running member's own: tasks report their progress, the
 * chain has none, and its `steps.total` grows while it runs.
 */
export function chainSteps(
  chain: BdJobChainSummaryResponse | BdJobChainResponse | null,
  options: BdJobChainStepsOptions = {},
): BdUiProgressStep[] {
  const { readingResult = false, labels } = options;
  if (!chain) {
    return [{ label: 'Verarbeitung wird gestartet', state: 'running' }];
  }

  const open = chain.status === 'OPEN';
  const current =
    open && chain.current && !isBookkeepingTask(chain.current.type)
      ? chain.current
      : null;
  const groups = groupByType('tasks' in chain ? chain.tasks : []);

  const steps: BdUiProgressStep[] =
    groups.length === 0 && current
      ? [
          {
            label: taskTypeLabel(current.type, labels),
            detail: stepDetail(undefined, current.progress?.message),
            state: 'running',
            percent: current.progress?.percent ?? null,
          },
        ]
      : groups.map((group) => {
          const state = stepState(group, open, current?.type);
          const progress =
            group.tasks.find((task) => task.status === 'IN_PROGRESS')
              ?.progress ??
            (current?.type === group.type ? current.progress : null);
          const running = state === 'running';
          return {
            label: taskTypeLabel(group.type, labels),
            detail: stepDetail(
              group.tasks,
              running ? progress?.message : undefined,
            ),
            state,
            percent: running ? (progress?.percent ?? null) : null,
          };
        });

  const ordered = steps.sort(
    (a, b) => STEP_ORDER[a.state] - STEP_ORDER[b.state],
  );
  return readingResult
    ? [...ordered, { label: 'Ergebnisse werden geladen', state: 'running' }]
    : ordered;
}

/**
 * Steps read top-down as done, running, waiting. Tasks come oldest first, but
 * a type's first member can predate the step that adds the rest (the cache
 * fill starts before the splitter fans it out), which put the running step
 * between finished ones. The sort is stable: creation order within a state.
 */
const STEP_ORDER: Readonly<Record<BdUiProgressStepState, number>> = {
  done: 0,
  failed: 0,
  running: 1,
  waiting: 2,
};

/**
 * A chain as a batch of attempts, for `bd-ui-progress-indicator`: one per
 * work member — bookkeeping members do not count — with failed and cancelled
 * members as failures. A summary without tasks falls back to `steps`.
 */
export function chainCounts(
  chain: BdJobChainSummaryResponse | BdJobChainResponse,
): BdProgressCounts {
  const state = chain.status === 'OPEN' ? 'running' : 'done';
  if (!('tasks' in chain)) {
    const { total, done, failed } = chain.steps;
    return { state, completed: done + failed, total, failed };
  }
  const work = chain.tasks.filter((task) => !isBookkeepingTask(task.type));
  const failed = work.filter(
    (task) => task.status === 'FAILED' || task.status === 'CANCELLED',
  ).length;
  const succeeded = work.filter((task) => task.status === 'SUCCESS').length;
  return { state, completed: succeeded + failed, total: work.length, failed };
}

/**
 * The member that failed the chain, if any. A FAILED member is the cause;
 * without one the chain ended because a member was cancelled or superseded.
 */
export function failedTask(
  chain: BdJobChainSummaryResponse | BdJobChainResponse | null,
): BdJobTaskStatusResponse | null {
  if (!chain || chain.status !== 'FAILED' || !('tasks' in chain)) {
    return null;
  }
  const work = chain.tasks.filter((task) => !isBookkeepingTask(task.type));
  return (
    work.find((task) => task.status === 'FAILED') ??
    work.find((task) => task.status === 'CANCELLED') ??
    null
  );
}

/**
 * Title of the error note: the step that ended the chain, "Analysen
 * vorberechnen fehlgeschlagen". The failed row shows where, the note's
 * message why; a summary without tasks names no step.
 */
export function failedStepTitle(
  chain: BdJobChainSummaryResponse | BdJobChainResponse | null,
  labels?: Readonly<Record<string, string>>,
): string {
  const task = failedTask(chain);
  return task
    ? `${taskTypeLabel(task.type, labels)} fehlgeschlagen`
    : 'Verarbeitung fehlgeschlagen';
}

export interface BdJobChainHeadings {
  /** While the chain is looked up or runs. */
  running: string;
  /** Once it succeeded; defaults to `running`. */
  succeeded?: string;
}

/** Heading of the step log for the chain's state; a FAILED chain reads "Verarbeitung abgebrochen". */
export function chainHeading(
  chain: BdJobChainSummaryResponse | BdJobChainResponse | null,
  headings: BdJobChainHeadings,
): string {
  switch (chain?.status) {
    case 'FAILED':
      return 'Verarbeitung abgebrochen';
    case 'SUCCESS':
      return headings.succeeded ?? headings.running;
    default:
      return headings.running;
  }
}

/**
 * User text for a task error. Built from `reason` alone: a user who does not
 * own the chain gets nothing else, and exception details never reach the UI.
 */
export function taskErrorText(error: BdJobTaskError | null | undefined): string {
  return (error && ERROR_TEXT[error.reason]) || ERROR_TEXT['handler-failed'];
}

interface TaskGroup {
  type: string;
  tasks: BdJobTaskStatusResponse[];
}

function groupByType(tasks: readonly BdJobTaskStatusResponse[]): TaskGroup[] {
  const groups = new Map<string, BdJobTaskStatusResponse[]>();
  for (const task of tasks) {
    if (isBookkeepingTask(task.type)) {
      continue;
    }
    const group = groups.get(task.type);
    if (group) {
      group.push(task);
    } else {
      groups.set(task.type, [task]);
    }
  }
  return [...groups].map(([type, members]) => ({ type, tasks: members }));
}

function stepState(
  group: TaskGroup,
  open: boolean,
  currentType: string | undefined,
): BdUiProgressStepState {
  const statuses = group.tasks.map((task) => task.status);
  if (statuses.some((status) => status === 'FAILED' || status === 'CANCELLED')) {
    return 'failed';
  }
  if (statuses.every((status) => status === 'SUCCESS')) {
    return 'done';
  }
  const started = statuses.some(
    (status) => status === 'IN_PROGRESS' || status === 'SUCCESS',
  );
  return open && (started || group.type === currentType) ? 'running' : 'waiting';
}

/** "1/3 · Standort 4 von 15": member count of a step with several, and the running member's message. */
function stepDetail(
  tasks: readonly BdJobTaskStatusResponse[] | undefined,
  message: string | undefined,
): string | undefined {
  const details: string[] = [];
  if (tasks && tasks.length > 1) {
    const done = tasks.filter((task) => task.status === 'SUCCESS').length;
    details.push(`${done}/${tasks.length}`);
  }
  if (message) {
    details.push(message);
  }
  return details.length > 0 ? details.join(' · ') : undefined;
}
